import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import type { useLingui } from "@lingui/react"
import {
  IconBrandTeams,
  IconBulb,
  IconFlame,
  IconMicrophone,
  IconMoodNeutral,
  IconPlayerRecord,
  IconTrendingDown,
  IconTrendingUp,
  IconWorld,
} from "@tabler/icons-react"
import { formatShortDate as fmtShortDate, formatTime as fmtTime } from "../../lib/format-date"
import { isProcessingStage, type TProcessingStage } from "../../lib/pipeline-progress"
import { sentimentLabelFor } from "../../lib/sentiment"
import { describeMeetingError } from "./error-reason"

// ---------------------------------------------------------------------------
// Sentiment chrome
// ---------------------------------------------------------------------------

interface ISentimentChrome {
  color: string
  cssColor: string
  textColor: string
  icon: React.ReactNode
}

// cssColor (the vivid accent bar + glow) uses var() so the Voxcanon resolver
// auto-themes. textColor is the WCAG-safe LABEL ink — the bright shade-4/5 fails
// on the card surface, so the label rides the Layer-2 --lex-text-* tokens while
// the decorative accent keeps the saturated hue.
export const SENTIMENT_CHROME: Record<string, ISentimentChrome> = {
  productive: {
    color: "success",
    cssColor: "var(--mantine-color-success-5)",
    textColor: "var(--lex-text-success)",
    icon: <IconTrendingUp size={12} />,
  },
  creative: {
    color: "brand",
    cssColor: "var(--mantine-color-brand-4)",
    textColor: "var(--lex-text-brand)",
    icon: <IconBulb size={12} />,
  },
  tense: {
    color: "danger",
    cssColor: "var(--mantine-color-danger-4)",
    textColor: "var(--lex-text-danger)",
    icon: <IconFlame size={12} />,
  },
  mixed: {
    color: "warning",
    cssColor: "var(--mantine-color-warning-5)",
    textColor: "var(--lex-text-warning)",
    icon: <IconTrendingDown size={12} />,
  },
  neutral: {
    color: "gray",
    cssColor: "var(--mantine-color-dimmed)",
    textColor: "var(--mantine-color-dimmed)",
    icon: <IconMoodNeutral size={12} />,
  },
}

export function sentimentLabel(key: string, i18n: ReturnType<typeof useLingui>["i18n"]): string {
  return sentimentLabelFor(i18n, key) ?? key
}

// ---------------------------------------------------------------------------
// Source icon
// ---------------------------------------------------------------------------

export function getSourceIcon(source: string): React.ReactNode {
  if (source === "browser") return <IconWorld size={12} />
  if (source === "transcription") return <IconMicrophone size={12} />
  if (source === "recording" || source === "recording+transcription") return <IconPlayerRecord size={12} />
  if (source === "teams") return <IconBrandTeams size={12} />
  return null
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export function formatTime(i18n: I18n, iso: string): string {
  if (!iso) return "—"
  return fmtTime(i18n, iso) || "—"
}

export function formatShortDate(i18n: I18n, iso: string): string {
  if (!iso) return "—"
  return fmtShortDate(i18n, iso) || "—"
}

// ---------------------------------------------------------------------------
// Processing-stage label
// ---------------------------------------------------------------------------

/**
 * Short label for the ghosted card's live progress row. Mirrors the Lexicanon
 * wording of `FinalizationProgress` exactly so the list card and the detail
 * finalizer read the same. Returns `null` for `null` / `"complete"` — a card
 * with nothing left to finalize has no stage to name (and solidifies).
 */
export function stageLabel(stage: TProcessingStage | null, i18n: I18n): string | null {
  switch (stage) {
    case "uploading":
      return i18n._(msg`Securing the recording`)
    case "transcribing":
      return i18n._(msg`Transcribing`)
    case "identifying_speakers":
      return i18n._(msg`Identifying speakers`)
    case "analyzing":
      return i18n._(msg`Generating insights`)
    default:
      return null
  }
}

const RAW_SPEAKER_LABEL = /^speaker[_\s-]*s?0*\d+$/i

function speakerCore(name: string): string {
  return name.startsWith("Remote::") ? name.slice("Remote::".length) : name
}

/** A person someone named, rather than a diarisation label such as "Speaker 1". */
export function isNamedSpeaker(name: string): boolean {
  const trimmed = speakerCore(name).trim()
  return trimmed.length > 0 && !RAW_SPEAKER_LABEL.test(trimmed)
}

/** First word of a display name, with the far-end channel prefix removed. */
export function speakerFirstName(name: string): string {
  const word = speakerCore(name).trim().split(/\s+/)[0]
  return word && word.length > 0 ? word : name
}

/**
 * Short reason on an ERROR list card. Known error text becomes a plain
 * sentence. When the row has no error text, name the last processing stage.
 */
export function meetingErrorReason(
  meeting: { error?: string | null; processing_stage?: string | null },
  i18n: I18n,
): string {
  const described = describeMeetingError(meeting.error, i18n)
  if (described) return described
  const stage = meeting.processing_stage?.trim()
  if (stage && stage !== "complete") {
    const labelled = isProcessingStage(stage) ? stageLabel(stage, i18n) : null
    if (labelled) return i18n._(msg`Failed while ${labelled}. Open for details.`)
    const readable = stage.replace(/_/g, " ")
    return i18n._(msg`Failed during ${readable}. Open for details.`)
  }
  return i18n._(msg`Something went wrong while processing.`)
}
